/**
 * Storage Service
 * Handles local data persistence using Capacitor Preferences
 */

import { Preferences } from '@capacitor/preferences';

class StorageService {
  /**
   * Save a value to local storage
   * @param {string} key - Storage key
   * @param {any} value - Value to store (will be JSON stringified)
   */
  async set(key, value) {
    try {
      await Preferences.set({
        key,
        value: JSON.stringify(value)
      });
      return true;
    } catch (error) {
      console.error(`Failed to save ${key}:`, error);
      return false;
    }
  }
  
  /**
   * Get a value from local storage
   * @param {string} key - Storage key
   * @returns {Promise<any>} Parsed value or null
   */
  async get(key) {
    try {
      const result = await Preferences.get({ key });
      return result.value ? JSON.parse(result.value) : null;
    } catch (error) {
      console.error(`Failed to get ${key}:`, error);
      return null;
    }
  }
  
  /**
   * Remove a value from local storage
   * @param {string} key - Storage key
   */
  async remove(key) {
    try {
      await Preferences.remove({ key });
      return true;
    } catch (error) {
      console.error(`Failed to remove ${key}:`, error);
      return false;
    }
  }
  
  /**
   * Clear all local storage
   */
  async clear() {
    try {
      await Preferences.clear();
      return true;
    } catch (error) {
      console.error('Failed to clear storage:', error);
      return false;
    }
  }
  
  /**
   * Get all keys in storage
   * @returns {Promise<string[]>} Array of keys
   */
  async keys() {
    try {
      const result = await Preferences.keys();
      return result.keys;
    } catch (error) {
      console.error('Failed to get keys:', error);
      return [];
    }
  }
  
  // ============ App-Specific Storage Methods ============
  
  /**
   * Save favorite RVs
   * @param {Array} favorites - Array of favorite RV objects
   */
  async saveFavorites(favorites) {
    return this.set('rv_favorites', favorites);
  }
  
  /**
   * Get favorite RVs
   * @returns {Promise<Array>} Array of favorite RVs
   */
  async getFavorites() {
    return (await this.get('rv_favorites')) || [];
  }
  
  /**
   * Add a favorite RV
   * @param {Object} rv - RV object to add
   */
  async addFavorite(rv) {
    const favorites = await this.getFavorites();
    const exists = favorites.some(f => f.id === rv.id);
    if (!exists) {
      favorites.push({
        ...rv,
        savedAt: new Date().toISOString()
      });
      await this.saveFavorites(favorites);
    }
    return favorites;
  }
  
  /**
   * Remove a favorite RV
   * @param {string} rvId - ID of RV to remove
   */
  async removeFavorite(rvId) {
    const favorites = await this.getFavorites();
    const filtered = favorites.filter(f => f.id !== rvId);
    await this.saveFavorites(filtered);
    return filtered;
  }
  
  /**
   * Check if an RV is favorited
   * @param {string} rvId - ID of RV to check
   */
  async isFavorite(rvId) {
    const favorites = await this.getFavorites();
    return favorites.some(f => f.id === rvId);
  }
  
  /**
   * Save quiz progress
   * @param {string} sessionId - Quiz session ID
   * @param {Object} progress - Quiz progress data
   */
  async saveQuizProgress(sessionId, progress) {
    return this.set(`quiz_progress_${sessionId}`, {
      ...progress,
      savedAt: new Date().toISOString()
    });
  }
  
  /**
   * Get quiz progress
   * @param {string} sessionId - Quiz session ID
   */
  async getQuizProgress(sessionId) {
    return this.get(`quiz_progress_${sessionId}`);
  }
  
  /**
   * Save recent searches
   * @param {Array} searches - Array of search strings
   */
  async saveRecentSearches(searches) {
    // Keep only last 10 searches
    const trimmed = searches.slice(0, 10);
    return this.set('recent_searches', trimmed);
  }
  
  /**
   * Get recent searches
   */
  async getRecentSearches() {
    return (await this.get('recent_searches')) || [];
  }
  
  /**
   * Add a recent search
   * @param {string} search - Search query to add
   */
  async addRecentSearch(search) {
    const searches = await this.getRecentSearches();
    // Remove if exists, then add to front
    const filtered = searches.filter(s => s !== search);
    filtered.unshift(search);
    await this.saveRecentSearches(filtered);
    return filtered.slice(0, 10);
  }
  
  /**
   * Save user session data
   * @param {Object} session - Session data including token
   */
  async saveSession(session) {
    return this.set('user_session', {
      ...session,
      savedAt: new Date().toISOString()
    });
  }
  
  /**
   * Get user session
   */
  async getSession() {
    return this.get('user_session');
  }
  
  /**
   * Clear user session
   */
  async clearSession() {
    return this.remove('user_session');
  }
  
  /**
   * Save app settings
   * @param {Object} settings - App settings object
   */
  async saveSettings(settings) {
    return this.set('app_settings', settings);
  }
  
  /**
   * Get app settings
   */
  async getSettings() {
    return (await this.get('app_settings')) || {
      notifications: true,
      darkMode: false,
      locationEnabled: true
    };
  }
  
  /**
   * Cache API response
   * @param {string} endpoint - API endpoint
   * @param {any} data - Response data
   * @param {number} ttlMinutes - Time to live in minutes
   */
  async cacheResponse(endpoint, data, ttlMinutes = 60) {
    const key = `cache_${endpoint.replace(/[^a-zA-Z0-9]/g, '_')}`;
    return this.set(key, {
      data,
      expiresAt: Date.now() + (ttlMinutes * 60 * 1000)
    });
  }
  
  /**
   * Get cached API response
   * @param {string} endpoint - API endpoint
   * @returns {Promise<any|null>} Cached data or null if expired/missing
   */
  async getCachedResponse(endpoint) {
    const key = `cache_${endpoint.replace(/[^a-zA-Z0-9]/g, '_')}`;
    const cached = await this.get(key);
    
    if (!cached) return null;
    if (Date.now() > cached.expiresAt) {
      await this.remove(key);
      return null;
    }
    
    return cached.data;
  }
}

export const storageService = new StorageService();
export default storageService;
